/**
 * ④PJ の変更一覧のレーン列の外に置く部品 — 要求の「背景」のモーダル。
 *
 * 前はここに横断的に関わるデータ（レーンに載らない用語・マスターの変更）の節も置いていたが、Dhik の
 * 指示（2026-09-29「PJ変更一覧の横断する変更の情報は丸ごと消してほしい」）で外した。その前に置いて
 * いた件数の帯（差分への主のボタン）も、2026-09-28「PJ変更一覧の差分の件数と差分を見るのコンポーネントは
 * 不要なので消して」で外している。
 *
 * レーン列が「この案件が何を触ったか」を出すのに対し、背景は**なぜ触ったか（要求と why）**を
 * 出す。要求のカード右下の副のボタン「背景」から開き、開いても絞り込みは動かない（引継書 §3.1。
 * 前はレーンの下の節に全部の要求を並べていたが、レーンと離れて読み合わせにくかった）。
 *
 * **変更の行は理由の欄を持たない**（決定 3）。なぜ変えたかは要求と why が持つので、理由を
 * 読む場所はこのモーダル 1 箇所である。
 *
 * 開いて見せるのは題（要求の全文）と「なぜ」だけで、目指す結果 ・ 受け入れ条件 ・ 根拠は畳み（既定は閉じる）、
 * 書いた人は題の `title`、根拠の取った日は根拠の行の `title` に載せる（Dhik 2026-09-29「本当に出すべき情報を
 * 1 or 2 個に」）。題の下の「要求の背景」は置かない — 「背景」を押して開いたので分かる。欄の名前を設計書の
 * 欄名に揃えるか・根拠の日付を UTC と JST のどちらで出すかは未決 U7。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ④PJ の変更一覧で、要求 1 件の背景（なぜ、畳んだ目指す結果・受け入れ条件・根拠）のモーダルを描く。
 * @graph-connects Modal [calls] モーダルの器
 */
import type { PjRequirement } from '@cortex/domain-spec-console';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/** 値が無い欄に出す印。 @graph-connects none */
const EMPTY_VALUE = '—';

/** 小見出しの見た目。 @graph-connects none */
const HEADING_CLASS =
  'mt-4 mb-1 text-xs font-bold tracking-wider text-[var(--color-text-secondary)] first:mt-0';

/** 本文の見た目。 @graph-connects none */
const TEXT_CLASS = 'text-sm leading-relaxed text-[var(--color-text-primary)]';

/** 畳んだ小見出し（押すと開く）。見出しと同じ字で、押せることを指の形で示す。 @graph-connects none */
const FOLD_CLASS = `${HEADING_CLASS} cursor-pointer`;

/**
 * 根拠の出どころを、見せる名前と URL に分けたもの。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 根拠の出どころの、見せる名前と URL の組
 * @graph-connects none
 */
export interface EvidenceSource {
  /** 見せる名前（URL の前まで） */
  readonly label: string;
  /** 出どころの URL。書かれていなければ null */
  readonly url: string | null;
  /** 案件の変更ログが出どころなら、その案件のキー（`AIRCLOSET-<番号>`）。ほかは null */
  readonly changeLogPj: string | null;
}

/**
 * 案件の変更ログを指す出どころ。書き手は「spec グラフ変更ログ AIRCLOSET-<番号> changeId=<id>」と書く。
 * 読取の名前（spec グラフ）と変更の行の ID は内部の実装の話なので、画面には案件のキーだけ残す
 * （Dhik の指示 2026-09-28「内部の実装の話が直接見えるとこに書いてあるのは全部消して」）。
 *
 * @graph-connects none
 */
const CHANGE_LOG_SOURCE = /^spec\s*グラフ\s*変更ログ\s+(AIRCLOSET-\d+)/u;

/**
 * 根拠の出どころを、見せる名前と URL に分ける。
 *
 * 出どころの欄は「案件仕様書 … シート https://…」のように名前と URL を 1 つの文字列で持つ。
 * 長い URL を本文に出すと根拠の中身より目立つので、名前だけを見せ、URL は指を載せたときに出す
 * （モックの置き方）。案件の変更ログは、案件のキーだけを取り出す（`changeLogPj`）。
 *
 * @param source 根拠の出どころ
 * @returns 見せる名前・URL・変更ログの案件のキー
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 根拠の出どころの文字列を、URL の前の名前と URL に分け、案件の変更ログなら案件のキーだけを取り出す。
 * @graph-connects none
 */
export function splitEvidenceSource(source: string): EvidenceSource {
  const changeLog = CHANGE_LOG_SOURCE.exec(source);
  if (changeLog !== null) return { label: source, url: null, changeLogPj: changeLog[1] ?? null };
  const at = source.search(/https?:\/\//u);
  if (at < 0) return { label: source, url: null, changeLogPj: null };
  const label = source.slice(0, at).trim();
  return { label: label === '' ? source : label, url: source.slice(at), changeLogPj: null };
}

/**
 * `PjRequirementWhyModal` の props。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 要求の背景のモーダルの入力（開く要求と閉じる口）
 * @graph-connects none
 */
export interface PjRequirementWhyModalProps {
  /** 背景を出す要求 */
  requirement: PjRequirement;
  /** 閉じる */
  onClose: () => void;
}

/**
 * 要求 1 件の背景のモーダル。
 *
 * why が複数あれば、なぜ・目指す結果は why ごとに段落を並べ、根拠は全部の why のぶんを並べる。
 * 書いた人は重複を畳んで並べる。why が無い要求も開ける — 何も書かれていないことが読める。
 *
 * 開いたらモーダルの中へフォーカスを移す。背後のカードにフォーカスが残ると、キーボードで読み進め
 * られない。
 *
 * @param props 開く要求と閉じる口
 * @returns 背景のモーダル
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 要求 1 件の why の「なぜ」をモーダルで描き、目指す結果・受け入れ条件・根拠を畳み、書いた人と根拠の取った日を title に載せる。
 * @graph-connects splitEvidenceSource [calls] 根拠の出どころを名前と URL に分ける
 */
export function PjRequirementWhyModal({ requirement, onClose }: PjRequirementWhyModalProps) {
  const { t } = useTranslation();
  const bodyRef = useRef<HTMLDivElement>(null);
  const evidences = requirement.whys.flatMap((why) =>
    why.evidences.map((evidence, index) => ({
      key: `${String(why.whyId)}-${String(index)}`,
      evidence,
    })),
  );
  const actors = [...new Set(requirement.whys.map((why) => why.actor).filter((a) => a !== ''))];

  useEffect(() => {
    bodyRef.current?.focus();
  }, []);

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={
        <span
          data-why-title
          title={actors.length === 0 ? undefined : `${t('pjLanes.whyActor')}: ${actors.join('、')}`}
        >
          {requirement.statement}
        </span>
      }
      data-requirement-why={requirement.requirementId}
    >
      <div ref={bodyRef} tabIndex={-1} className="outline-none">
        <h3 className={HEADING_CLASS}>{t('pjLanes.whyStatement')}</h3>
        <Paragraphs values={requirement.whys.map((why) => why.statement)} field="statement" />
        <details data-why-fold="outcome">
          <summary className={FOLD_CLASS}>{t('pjLanes.whyOutcome')}</summary>
          <Paragraphs values={requirement.whys.map((why) => why.outcome)} field="outcome" />
        </details>
        <details data-why-fold="acceptance">
          <summary className={FOLD_CLASS}>{t('pjLanes.whyAcceptance')}</summary>
          <p data-why-field="acceptance" className={TEXT_CLASS}>
            {requirement.acceptance}
          </p>
        </details>
        <details data-why-fold="evidence">
          <summary className={FOLD_CLASS}>
            {t('pjLanes.whyEvidence')}
            <span className="ml-1.5 font-normal tabular-nums text-[var(--color-text-muted)]">
              {t('pjLanes.whyEvidenceCount', { count: evidences.length })}
            </span>
          </summary>
          {evidences.length === 0 ? (
            <p className={TEXT_CLASS}>{EMPTY_VALUE}</p>
          ) : (
            evidences.map(({ key, evidence }) => {
              const source = splitEvidenceSource(evidence.source);
              return (
                <div
                  key={key}
                  data-why-evidence
                  title={t('pjLanes.whyObservedAt', { date: evidence.observedAt.slice(0, 10) })}
                  className="mt-1.5 rounded-sm border border-[var(--color-border)] px-2.5 py-2"
                >
                  <p className="mb-0.5 text-xs text-[var(--color-text-muted)]">
                    <span
                      className="font-bold text-[var(--color-text-secondary)]"
                      title={source.url ?? undefined}
                    >
                      {source.changeLogPj === null
                        ? source.label
                        : t('pjLanes.whyChangeLog', { pj: source.changeLogPj })}
                    </span>
                  </p>
                  <p className={`${TEXT_CLASS} whitespace-pre-wrap`}>{evidence.value}</p>
                </div>
              );
            })
          )}
        </details>
      </div>
    </Modal>
  );
}

/** `Paragraphs` の props。 @graph-connects none */
interface ParagraphsProps {
  /** 並べる文 */
  values: readonly string[];
  /** 欄の名前（DOM の目印） */
  field: string;
}

/**
 * 文を段落で並べる。1 つも無ければ「—」。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business why ごとの文を段落で並べ、無ければ空の印を出す。
 * @graph-connects none
 */
function Paragraphs({ values, field }: ParagraphsProps) {
  if (values.length === 0) {
    return (
      <p data-why-field={field} className={TEXT_CLASS}>
        {EMPTY_VALUE}
      </p>
    );
  }
  return (
    <>
      {values.map((value, index) => (
        <p key={`${field}-${String(index)}`} data-why-field={field} className={TEXT_CLASS}>
          {value}
        </p>
      ))}
    </>
  );
}
